import { composeReactEmail } from '@react-email/editor/core';
import { StarterKit } from '@react-email/editor/extensions';
import { EmailTheming, extendTheme } from '@react-email/editor/plugins';

import type { ReactNode } from 'react';
import type { EmailEditorProps, EmailEditorRef } from '@react-email/editor';
import type { EmailBody } from './schema';
import {
  OUTBOUND_BODY_MAX_BYTES,
  getOutboundBodyByteLength,
} from '@minty/shared/email';
import { stripRenderWrapper } from '@minty/shared/react-email';

/** Neither is exported by the package, so both are read back off what they
 *  belong to. */
type Editor = NonNullable<EmailEditorRef['editor']>;
type SerializerPlugin = NonNullable<
  NonNullable<Parameters<typeof EmailTheming.configure>[0]>['serializerPlugin']
>;

/**
 * What a typed reply looks like on the wire: the text, in one element that says
 * what language it is and which way it runs. No document, because a person
 * writing to a person does not send one — Gmail's composer emits the same bare
 * `<div dir="ltr">`, and every client's parser supplies the rest.
 *
 * The wrapper itself is load-bearing beyond the attributes: with the children
 * bare, a blockquote somebody typed as their last paragraph sits at the top
 * level, which is exactly the shape quote detection reads as a quoted chain.
 */
function BaseTemplate({ children }: { children: ReactNode }) {
  return (
    <div dir="ltr" lang="pl">
      {children}
    </div>
  );
}

/** Only the template is ours. `getNodeStyles` is what inlines the theme onto
 *  every node, and replacing the plugin replaces both. */
const serializerPlugin: SerializerPlugin = {
  BaseTemplate,
  getNodeStyles: (...args) =>
    EmailTheming.options.serializerPlugin.getNodeStyles(...args),
};

export const EDITOR_EXTENSIONS: NonNullable<EmailEditorProps['extensions']> = [
  /** `Container` force-wraps the document in a 600px centred table — a
   *  newsletter's shape, imposed on a reply. */
  StarterKit.configure({ Container: false }),
  EmailTheming.configure({
    serializerPlugin,
    theme: extendTheme('basic', {
      container: {
        align: 'left',
        paddingBottom: '16px',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '16px',
      },
    }),
  }),
];

/**
 * The message as it will be sent, both parts from one serialization.
 *
 * `composeReactEmail` rather than the ref's `getEmail()`, for the third field
 * the ref's type hides: `html` is the Prettier-formatted copy, which the
 * package's own comment says not to send, and `unformattedHtml` is the same
 * markup without the indentation.
 */
export async function composeEmailBody(editor: Editor): Promise<EmailBody> {
  const { text, unformattedHtml } = await composeReactEmail({ editor });

  return { html: stripRenderWrapper(unformattedHtml), text };
}

type EmailBodyValidationResult =
  | { ok: true; body: EmailBody }
  | { ok: false; failure: { kind: 'empty' | 'too-large' } };

export function validateEmailBody(
  body: EmailBody,
  required: boolean,
): EmailBodyValidationResult {
  const normalizedBody = {
    html: body.html.trim(),
    text: body.text.trim(),
  };

  if (required && !normalizedBody.text) {
    return { ok: false, failure: { kind: 'empty' } };
  }

  if (getOutboundBodyByteLength(normalizedBody) > OUTBOUND_BODY_MAX_BYTES) {
    return { ok: false, failure: { kind: 'too-large' } };
  }

  return { ok: true, body };
}

/** A generated draft, preserving paragraph breaks without doubling spacing. */
export function toDraftContent(
  text: string,
): NonNullable<EmailEditorProps['content']> {
  const paragraphs = text
    .trim()
    .split(/\n\s*\n+/)
    .map((paragraph) => paragraph.split(/\n/).filter((line) => line !== ''))
    .filter((lines) => lines.length > 0);

  return {
    type: 'doc',
    content: paragraphs.map((lines) => ({
      type: 'paragraph',
      content: lines.flatMap((line, index) =>
        index === 0
          ? [{ type: 'text', text: line }]
          : [{ type: 'hardBreak' }, { type: 'text', text: line }],
      ),
    })),
  };
}
